import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { CompanyNotaFiscalListItem } from './types';

export interface CompanyNotaFiscalMostrarDialogProps {
  open: boolean;
  nota: CompanyNotaFiscalListItem | null;
  onClose: () => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function temPdf(nota: CompanyNotaFiscalListItem): boolean {
  return nota.temPdf === true || Boolean(nota.pdfNome);
}

function arquivoUrl(nota: CompanyNotaFiscalListItem): string {
  return `/api/v1/companies/${nota.empresaId}/notas-fiscais/${nota.id}/file`;
}

/**
 * Consulta da nota (ação Mostrar). A guia do PDF mostra o arquivo na tela
 * para o administrativo validar; o download é opcional.
 */
export function CompanyNotaFiscalMostrarDialog({
  open,
  nota,
  onClose,
}: CompanyNotaFiscalMostrarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [aba, setAba] = useState<'nota' | 'pdf'>('nota');

  function fechar(): void {
    setAba('nota');
    onClose();
  }

  const pdfSrc = nota && temPdf(nota) ? arquivoUrl(nota) : null;

  const emitida = nota?.data && dayjs(nota.data).isValid() ? dayjs(nota.data).format('DD/MM/YYYY') : '—';
  const campos: Array<[string, string]> = nota
    ? [
        [t('features.empresa.notasFiscais.columns.empresa'), nota.empresaNome || '—'],
        [t('features.empresa.notasFiscais.columns.numero'), String(nota.numero ?? nota.id)],
        [t('features.empresa.notasFiscais.columns.rps'), nota.numeroRPS != null ? String(nota.numeroRPS) : '—'],
        [t('features.empresa.notasFiscais.columns.emitidaEm'), emitida],
        [t('features.empresa.notasFiscais.columns.periodo'), nota.periodoLabel ?? '—'],
        [t('features.empresa.notasFiscais.columns.cliente'), nota.clienteNome ?? '—'],
        [t('features.empresa.notasFiscais.columns.rc'), nota.requisicaoCompraReferencia ?? '—'],
        [t('features.empresa.notasFiscais.columns.colaborador'), nota.colaboradorNome ?? '—'],
        [t('features.empresa.notasFiscais.columns.valor'), formatBRL(nota.valor)],
        [t('features.empresa.notasFiscais.columns.imposto'), formatBRL(nota.valorImposto)],
        [t('features.empresa.notasFiscais.columns.valorLiquido'), formatBRL(nota.valorLiquido)],
      ]
    : [];

  return (
    <Dialog open={open} onClose={fechar} maxWidth="lg" fullWidth>
      <DialogTitle>{t('features.empresa.notasFiscais.mostrar.title')}</DialogTitle>
      <Tabs value={aba} onChange={(_, value: 'nota' | 'pdf') => setAba(value)} sx={{ px: 3 }}>
        <Tab value="nota" label={t('features.empresa.notasFiscais.mostrar.abaNota')} />
        <Tab value="pdf" label={t('features.empresa.notasFiscais.mostrar.abaPdf')} />
      </Tabs>
      <DialogContent dividers sx={{ minHeight: 420 }}>
        {aba === 'nota' && (
          <Stack spacing={1}>
            {campos.map(([rotulo, valor]) => (
              <Stack key={rotulo} direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>
                  {rotulo}
                </Typography>
                <Typography variant="body2">{valor}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
        {aba === 'pdf' && nota && !temPdf(nota) && (
          <Typography variant="body2" color="text.secondary">
            {t('features.empresa.notasFiscais.mostrar.semPdf')}
          </Typography>
        )}
        {aba === 'pdf' && pdfSrc && (
          <Box
            component="iframe"
            src={pdfSrc}
            title={t('features.empresa.notasFiscais.mostrar.abaPdf')}
            sx={{ width: '100%', height: '70vh', border: 0 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {aba === 'pdf' && pdfSrc && nota && (
          <Button
            component="a"
            href={pdfSrc}
            download={nota.pdfNome || `nf-${nota.numero ?? nota.id}.pdf`}
          >
            {t('features.empresa.notasFiscais.actions.downloadItem')}
          </Button>
        )}
        <Button onClick={fechar}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
